import type { FC } from "react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";

import { Button } from "@src/components";
import { APP_ROUTES } from "@src/core/constants";
import { useSeo } from "@src/core/hooks";
import { Authentication } from "@src/core/services";
import { Container, ContentContainer, PageTitle } from "@src/core/styles";
import { ApiTokensPanel } from "../settings-page/api-tokens-panel";
import {
  ArticleBody,
  Section,
  SectionTitle,
  ToolContainer,
  ToolIntro,
  ToolSubtitle,
} from "@src/pages/dev-tools-shared";

const CodeBlock: FC<{ value: string }> = ({ value }) => {
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      enqueueSnackbar("Скопировано", {
        variant: "reportComplete",
        className: "success",
        preventDuplicate: true,
      });
    } catch {
      enqueueSnackbar("Не удалось скопировать", {
        variant: "reportComplete",
        className: "error",
        preventDuplicate: true,
      });
    }
  };

  return (
    <div>
      <pre
        style={{
          margin: 0,
          padding: "16px",
          borderRadius: "12px",
          background: "#1A1C19",
          color: "#E2E3DD",
          overflowX: "auto",
          overflowY: "hidden",
          fontSize: "13px",
          lineHeight: 1.5,
          whiteSpace: "pre",
        }}
      >
        <code>{value}</code>
      </pre>
      <div style={{ marginTop: "8px" }}>
        <Button
          text="Копировать"
          type="button"
          variant="outlined"
          borderradius="L"
          onClick={handleCopy}
        />
      </div>
    </div>
  );
};

export const ConnectPage: FC = () => {
  const [tab, setTab] = useState<"remote" | "stdio">("remote");
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const mcpUrl = `${origin}/api/v1/mcp`;
  const apiBase = `${origin}/api/v1`;

  const { data: info, isError } = useQuery({
    queryKey: ["info"],
    queryFn: () => Authentication.info(),
    retry: false,
  });
  const isLoggedIn = !!info?.data && !isError;

  useSeo({
    title: "MCP для агентов — подключить Cursor и Claude к LightBox API",
    description:
      "Подключите Cursor или Claude к LightBox API по MCP: агент создаёт workspace, mock-роуты и отдаёт готовый URL.",
    canonical: APP_ROUTES.CONNECT,
  });

  const remoteConfig = useMemo(
    () => `{
  "mcpServers": {
    "lightbox": {
      "url": "${mcpUrl}",
      "headers": {
        "Authorization": "Bearer PASTE_TOKEN_HERE"
      }
    }
  }
}`,
    [mcpUrl]
  );

  const stdioConfig = useMemo(
    () => `{
  "mcpServers": {
    "lightbox": {
      "command": "node",
      "args": ["mcp/index.mjs"],
      "env": {
        "LIGHTBOX_BASE_URL": "${apiBase}",
        "LIGHTBOX_API_TOKEN": "PASTE_TOKEN_HERE"
      }
    }
  }
}`,
    [apiBase]
  );

  return (
    <Container>
      <ContentContainer>
        <ToolContainer>
          <ToolIntro>
            <PageTitle>Подключение агентов (MCP)</PageTitle>
            <ToolSubtitle>
              Агент в Cursor или Claude может создавать workspace и mock-роуты в вашем аккаунте,
              а затем вызывать полученный URL из фронтенда или тестов.
            </ToolSubtitle>
          </ToolIntro>

          <Section>
            <SectionTitle>1. Выпустите токен</SectionTitle>
            <ArticleBody>
              {isLoggedIn ? (
                <ApiTokensPanel />
              ) : (
                <p>
                  Чтобы выпустить токен,{" "}
                  <Link to={APP_ROUTES.LOGIN}>войдите</Link> в аккаунт. Затем вернитесь на эту
                  страницу.
                </p>
              )}
            </ArticleBody>
          </Section>

          <Section>
            <SectionTitle>2. Добавьте сервер в Cursor</SectionTitle>
            <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
              <Button
                text="Remote URL"
                type="button"
                variant={tab === "remote" ? "filled" : "outlined"}
                borderradius="L"
                onClick={() => setTab("remote")}
              />
              <Button
                text="Local stdio"
                type="button"
                variant={tab === "stdio" ? "filled" : "outlined"}
                borderradius="L"
                onClick={() => setTab("stdio")}
              />
            </div>
            <ArticleBody>
              {tab === "remote" ? (
                <>
                  <p>
                    Вставьте в <code>~/.cursor/mcp.json</code> (или в MCP settings проекта).
                    Замените <code>PASTE_TOKEN_HERE</code> на токен из шага 1.
                  </p>
                  <CodeBlock value={remoteConfig} />
                </>
              ) : (
                <>
                  <p>
                    Для локального stdio-сервера. <code>args</code> — путь к{" "}
                    <code>mcp/index.mjs</code> относительно корня проекта.
                  </p>
                  <CodeBlock value={stdioConfig} />
                </>
              )}
            </ArticleBody>
          </Section>

          <Section>
            <SectionTitle>3. Что умеет агент</SectionTitle>
            <ArticleBody>
              <ul>
                <li>
                  <code>create_workspace</code> / <code>list_workspaces</code>
                </li>
                <li>
                  <code>create_mock_route</code> / <code>update_mock_route</code> /{" "}
                  <code>list_routes</code>
                </li>
                <li>
                  <code>get_mock_url</code> — готовый URL мока
                </li>
                <li>
                  <code>get_route_logs</code> — почему клиентский запрос не попал в мок
                </li>
              </ul>
              <p>
                Пример: «создай workspace checkout-api, мок POST /orders → 201 с JSON и дай URL».
              </p>
            </ArticleBody>
          </Section>
        </ToolContainer>
      </ContentContainer>
    </Container>
  );
};
